import React, { useEffect, useRef, useState } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ScrollableCard from "../../../../SharedComponents/ScrollableCard";
import ConfirmationModal from "../../../../SharedComponents/GeneralModals";
import CommentList from "./CommentList";
import { deleteComment, upsertComment } from "../../Store/Thunk";
import { ClearSelectedComment } from "../../Store";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";

const Comment = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [confirmationModalIsShown, setConfirmationModalIsShown] = useState(false);
  const [commentIdToBeDeleted, setCommentIdToBeDeleted] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [commentCreationInput, setCommentCreationInput] = useState("");

  // ref for the textarea so we can adjust its height
  const textareaRef = useRef(null);
  const selectedComment = useSelector((state) => state.candidate.selectedComment);
  const comments = useSelector((state) => state.candidate.comments);
  const companyUserId = useSelector((state) => state.auth.user.companyUser.companyUserId);
  const candidate = useSelector((state) => state.candidate.candidate);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1000);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // whenever the input text changes, recalculate the textarea's height
  useEffect(() => {
    if (textareaRef.current) {
      // reset height to auto so scrollHeight shrinks properly when deleting
      textareaRef.current.style.height = "auto";
      // set height based on scrollHeight
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  }, [commentCreationInput]);

  const HandleSubmit = (e) => {
    e.preventDefault();
    if (selectedComment?.commentId) {
      dispatch(
        upsertComment({
          content: commentCreationInput,
          commentId: selectedComment.commentId,
        })
      );
    } else {
      dispatch(
        upsertComment({
          content: commentCreationInput,
          companyUserId: companyUserId,
          candidateId: candidate.candidateId,
        })
      );
    }
    dispatch(ClearSelectedComment());
  };

  useEffect(() => {
    if (selectedComment?.content) {
      setCommentCreationInput(selectedComment.content);
    } else {
      setCommentCreationInput("");
    }
  }, [selectedComment?.content]);

  const ConfirmationFunction = () => {
    if (commentIdToBeDeleted) {
      dispatch(deleteComment({ id: commentIdToBeDeleted }));
    }
    setConfirmationModalIsShown(false);
  };

  const DismissFunction = () => {
    setConfirmationModalIsShown(false);
    setCommentIdToBeDeleted(null);
  };

  return (
    <>
      <ConfirmationModal
        isShown={confirmationModalIsShown}
        setIsShown={setConfirmationModalIsShown}
        text={t("deleteCommentModalText")}
        confirmationFunction={ConfirmationFunction}
        dismissFunction={DismissFunction}
      />

      <div className="custom-candidate-margin pt-4">
        <ScrollableCard top={0} emptySpaceHeight={0} bottom={0} maximumHeight={isMobile && 600}>
          <Row className="mx-0 mt-2">
            <Col>
              <Card
                className="default-card py-3 px-2 px-md-4"
                style={{
                  backgroundColor: comments.length > 0 ? "#fff" : "#f7f7f7",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* make the comment list scrollable within the Card,
                    leaving room for the sticky footer below */}
                <div
                  style={{
                    overflowY: "auto",
                    flexGrow: 1,
                    paddingBottom: "1rem", // ensure space for the sticky footer
                  }}
                >
                  <CommentList
                    setCommentIdToBeDeleted={setCommentIdToBeDeleted}
                    setConfirmationModalIsShown={setConfirmationModalIsShown}
                  />
                </div>

                {/* add-comment section, sticky to bottom */}
              </Card>
            </Col>
          </Row>

          <Form
            onSubmit={HandleSubmit}
            style={{
              position: "sticky",
              bottom: 0,

              marginTop: -20,
              backgroundColor: comments.length > 0 ? "#fff" : "#f7f7f7",
              borderColor: "#e7ecef",
            }}
          >
            <div style={{ backgroundColor: "#F8F9FA", paddingBottom: 20 }}>
              <Row
                className=" default-card no-top-shadow     border-radius-top-0 py-2 justify-content-center"
                style={{ marginLeft: 12, marginRight: 12 }}
              >
                <Col>
                  <Form.Control
                    as="textarea"
                    ref={textareaRef}
                    rows={1} // start at a single row
                    className="default-text-input text-color-gray-dark fw-normal bg-white"
                    style={{
                      width: "100%",
                      minHeight: "100%",
                      overflow: "hidden",
                      resize: "none",
                    }}
                    onChange={(e) => setCommentCreationInput(e.target.value)}
                    value={commentCreationInput}
                    placeholder={t("enterCommentPlaceholder")}
                  />
                </Col>
                <Col xs="auto" className="ps-0 align-content-end">
                  {selectedComment?.content && (
                    <Row className="mx-0 mb-2">
                      <Button
                        onClick={() => {
                          dispatch(ClearSelectedComment());
                          setCommentCreationInput("");
                        }}
                        size="sm"
                        className="button secondary px-3"
                      >
                        {t("cancelCommentUpdate")}
                      </Button>
                    </Row>
                  )}
                  <Row className="mx-0">
                    <Button
                      type="submit"
                      size="sm"
                      className="button secondary px-3"
                      disabled={!commentCreationInput || selectedComment?.content === commentCreationInput}
                    >
                      {selectedComment?.content ? t("updateComment") : t("addComment")}
                    </Button>
                  </Row>
                </Col>
              </Row>
            </div>
          </Form>
        </ScrollableCard>
      </div>
    </>
  );
};

export default Comment;
